import React, { useCallback, useMemo } from 'react';
import { Avatar, Button, Typography, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { shallowEqual } from 'react-redux';
import { logoutUser } from '../../services/services';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { clearCart } from '../../redux/slices/shoppingCartSlice';
import { clearLiked } from '../../redux/slices/likedProductSlice';
import './userSidebar.scss';

const { Text } = Typography;

const UserSidebar = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
      cartItemsCount,
      cartItemsLength,
      likedProductCount,
      likedProductsLength,
      user,
    } = useAppSelector((state) => ({
      user: state.user.user,
      cartItemsCount: state.shoppingCart.addedTooCartProductCountAndIds.count,
      cartItemsLength: state.shoppingCart.cartItems.length,
      likedProductCount: state.likedProduct.likedProductCountAndIds.count,
      likedProductsLength: state.likedProduct.likedProducts.length,
    }), shallowEqual);
    const hasCartItems = useMemo(
      () => cartItemsCount > 0 || cartItemsLength > 0,
      [cartItemsCount, cartItemsLength],
    );
    const hasLikedProducts = useMemo(
      () => likedProductCount > 0 || likedProductsLength > 0,
      [likedProductCount, likedProductsLength],
    );

    const handleLogout = useCallback(() => {
        dispatch(logoutUser())
          .unwrap()
          .then(() => {
            dispatch(clearCart());
            dispatch(clearLiked());
            navigate('/');
          })
          .catch((error) => {
            console.error('Logout failed:', error);
          });
    }, [dispatch, navigate]);

    const handleCartNavigate = useCallback(() => {
      if (hasCartItems) {
        navigate('/cart');
        return;
      }

      message.warning('Please add a product to the cart.');
    }, [hasCartItems, navigate]);

    const handleLikedNavigate = useCallback(() => {
      if (hasLikedProducts) {
        navigate('/product/liked');
        return;
      }

      message.warning('Please like a product before opening your wishlist.');
    }, [hasLikedProducts, navigate]);

  return (
    <div className="user-sidebar">
      <div className="user-sidebar-header">
      <Link to="/UserPage">
        <Avatar
          className="user-sidebar-avatar"
          size={64}
          src={user?.avatar?.publicUrl || undefined}
          icon={<UserOutlined />}
        />
      </Link>
        <Text strong>{user?.name || 'No name'}</Text>
      </div>
      <hr className="divider" />
      <ul className="user-sidebar-nav">
        <li>
            <Link className="ant-btn ant-btn-link user-sidebar-nav-link" to="/UserPage/order-history">
              Order History
            </Link>
        </li>
        <li>
            <Link className="ant-btn ant-btn-link user-sidebar-nav-link" to="/UserPage/shipping-address">
              Shipping Address
            </Link>
        </li>
        <li>
            <Link className="ant-btn ant-btn-link user-sidebar-nav-link" to="/UserPage">
              Settings & Preferences
            </Link>
        </li>
        <li>
            <Button type="link" onClick={handleCartNavigate}>Shopping Bag</Button>
        </li>
        <li>
            <Button type="link" onClick={handleLikedNavigate}>Wish List</Button>
        </li>
        <li>
            <Button type="link">Refer a Friend</Button>
        </li>
        {user?.isAdmin && (
          <>
            <hr className="divider" />
            <li className="user-sidebar-admin-title">
              <Text type="secondary">Admin</Text>
            </li>
            <li>
              <Link className="ant-btn ant-btn-link user-sidebar-nav-link" to="/UserPage/admin/orders">
                All orders
              </Link>
            </li>
            <li>
              <Link className="ant-btn ant-btn-link user-sidebar-nav-link" to="/UserPage/admin/products">
                Manage Products
              </Link>
            </li>
            <li>
              <Link className="ant-btn ant-btn-link user-sidebar-nav-link" to="/UserPage/admin/products/create">
                Create Product
              </Link>
            </li>
            <li>
              <Link className="ant-btn ant-btn-link user-sidebar-nav-link" to="/UserPage/admin/users">
                Manage Users
              </Link>
            </li>
          </>
        )}
        <hr className="divider" />
        <li className="user-sidebar-signout-item">
          <button type="button" className="user-sidebar-signout" onClick={handleLogout}>
            Sign Out
          </button>
        </li>
      </ul>
    </div>
  );
};

export default React.memo(UserSidebar);
